import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Phone, Lock, Eye, EyeOff, ChevronDown, ArrowLeft, RefreshCw } from 'lucide-react';
import kitchenApi from '../services/kitchenApiService';
import { useKitchenAuth } from '../context/KitchenAuthContext';

const COUNTRY_CODES = [
  { code: '+1',   flag: '🇺🇸', label: 'US' },
  { code: '+91',  flag: '🇮🇳', label: 'IN' },
  { code: '+44',  flag: '🇬🇧', label: 'UK' },
  { code: '+61',  flag: '🇦🇺', label: 'AU' },
  { code: '+971', flag: '🇦🇪', label: 'AE' },
  { code: '+966', flag: '🇸🇦', label: 'SA' },
  { code: '+65',  flag: '🇸🇬', label: 'SG' },
];

const OTP_LEN = 6;

export default function KitchenLoginPage() {
  const { login } = useKitchenAuth();
  const navigate   = useNavigate();

  // Tabs: 'otp' | 'staff'
  const [tab, setTab] = useState('otp');

  // ── OTP state ──────────────────────────────────────────────────
  const [country,      setCountry]      = useState(COUNTRY_CODES[0]);
  const [showCC,       setShowCC]       = useState(false);
  const [phone,        setPhone]        = useState('');
  const [step,         setStep]         = useState('phone'); // 'phone' | 'otp'
  const [otp,          setOtp]          = useState(Array(OTP_LEN).fill(''));
  const [sending,      setSending]      = useState(false);
  const [verifying,    setVerifying]    = useState(false);
  const [countdown,    setCountdown]    = useState(0);
  const [otpError,     setOtpError]     = useState('');
  const [devOtp,       setDevOtp]       = useState('');
  const otpRefs = useRef([]);

  // ── Staff state ────────────────────────────────────────────────
  const [identifier, setIdentifier]   = useState('');
  const [password,   setPassword]     = useState('');
  const [showPw,     setShowPw]       = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError,   setStaffError]   = useState('');

  // ── Countdown ─────────────────────────────────────────────────
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const cleanDigits = phone.replace(/\D/g, '');
  const phoneValid  = cleanDigits.length >= 7;

  // ── Send OTP ──────────────────────────────────────────────────
  const sendOtp = async () => {
    if (!phoneValid) return;
    setSending(true);
    setOtpError('');
    const res = await kitchenApi.sendKitchenOTP(cleanDigits, country.code);
    setSending(false);
    if (res.success) {
      setStep('otp');
      setCountdown(60);
      setOtp(Array(OTP_LEN).fill(''));
      setDevOtp(res.data?.dev_otp || res.data?.otp || '');
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } else {
      setOtpError(res.error || 'Failed to send OTP. Please try again.');
    }
  };

  // ── OTP input handlers ─────────────────────────────────────────
  const handleOtpChange = (idx, val) => {
    const d = val.replace(/\D/g, '').slice(-1);
    const next = [...otp];
    next[idx] = d;
    setOtp(next);
    if (d && idx < OTP_LEN - 1) otpRefs.current[idx + 1]?.focus();
  };
  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) otpRefs.current[idx - 1]?.focus();
  };
  const handleOtpPaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LEN);
    if (!text) return;
    const next = [...otp];
    text.split('').forEach((c, i) => { if (i < OTP_LEN) next[i] = c; });
    setOtp(next);
    otpRefs.current[Math.min(text.length, OTP_LEN - 1)]?.focus();
    e.preventDefault();
  };

  // ── Verify OTP ─────────────────────────────────────────────────
  const verifyOtp = async () => {
    const code = otp.join('');
    if (code.length < OTP_LEN) { setOtpError('Enter the full 6-digit code.'); return; }
    setVerifying(true);
    setOtpError('');
    const res = await kitchenApi.verifyKitchenOTP(cleanDigits, country.code, code);
    setVerifying(false);
    if (res.success) {
      const d = res.data?.data || res.data;
      if (d?.is_new_user) {
        // New user → go to registration
        navigate('/kitchen/register', { state: { verification_token: d.verification_token, phone: cleanDigits, countryCode: country.code } });
      } else {
        const token = d?.access_token;
        if (token) {
          kitchenApi.setToken(token);
          if (d?.refresh_token) kitchenApi.setRefreshToken(d.refresh_token);
          kitchenApi.saveUserMeta(d?.user);
          login(d?.user || d);
          navigate('/kitchen/dashboard');
        } else {
          setOtpError('Login failed — no token received. Please try again.');
        }
      }
    } else {
      setOtpError(res.error || 'Invalid OTP. Please try again.');
    }
  };

  // ── Staff login ────────────────────────────────────────────────
  const handleStaffLogin = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) { setStaffError('Please enter your credentials.'); return; }
    setStaffLoading(true);
    setStaffError('');
    const res = await kitchenApi.kitchenStaffLogin({
      login_type:        'password',
      username_or_email: identifier.trim(),
      password,
    });
    setStaffLoading(false);
    if (res.success) {
      const d = res.data?.data || res.data;
      login(d?.user || d);
      navigate('/kitchen/dashboard');
    } else {
      setStaffError(res.error || 'Invalid credentials. Please try again.');
    }
  };

  return (
    <div className="k-auth-wrap" style={{ background: 'linear-gradient(135deg, #FFF7F3 0%, #F8FAFC 50%, #F0F4FF 100%)' }}>
      <div className="k-auth-box">
        {/* Logo */}
        <div className="k-auth-logo">
          <div className="k-auth-logo-icon">🍛</div>
          <div className="k-auth-title">Biryani Kitchen</div>
          <div className="k-auth-subtitle">Sign in to your kitchen portal</div>
        </div>

        {/* Tabs */}
        <div className="k-tabs" style={{ marginBottom: 24 }}>
          <button className={`k-tab${tab === 'otp' ? ' active' : ''}`} onClick={() => { setTab('otp'); setStep('phone'); setOtpError(''); }}>
            📱 Phone OTP
          </button>
          <button className={`k-tab${tab === 'staff' ? ' active' : ''}`} onClick={() => { setTab('staff'); setStaffError(''); }}>
            👤 Staff Login
          </button>
        </div>

        {/* ── OTP Tab ── */}
        {tab === 'otp' && step === 'phone' && (
          <div>
            <div className="k-form-group">
              <label className="k-label required">Phone Number</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {/* Country picker */}
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setShowCC(p => !p)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '10px 10px', border: '1.5px solid var(--k-border)',
                      borderRadius: 'var(--k-radius-sm)', background: 'var(--k-surface)',
                      fontSize: 14, cursor: 'pointer', height: '100%', whiteSpace: 'nowrap',
                    }}
                  >
                    {country.flag} {country.code} <ChevronDown size={14} />
                  </button>
                  {showCC && (
                    <div className="k-dropdown-menu" style={{ top: 'calc(100% + 4px)', left: 0, right: 'auto', minWidth: 120 }}>
                      {COUNTRY_CODES.map(cc => (
                        <button
                          key={cc.code + cc.label}
                          className="k-dropdown-item"
                          onClick={() => { setCountry(cc); setShowCC(false); }}
                        >
                          {cc.flag} {cc.code} ({cc.label})
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  className="k-input"
                  type="tel"
                  placeholder="Phone number"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && phoneValid && sendOtp()}
                  autoFocus
                />
              </div>
              {otpError && <div className="k-error-text">{otpError}</div>}
            </div>
            <button
              className="k-btn k-btn-primary"
              style={{ width: '100%' }}
              disabled={!phoneValid || sending}
              onClick={sendOtp}
            >
              {sending ? <><span className="k-spinner k-spinner-sm" style={{ borderTopColor: '#fff' }} /> Sending...</> : 'Send OTP →'}
            </button>
          </div>
        )}

        {tab === 'otp' && step === 'otp' && (
          <div>
            <button
              type="button"
              style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--k-text-sub)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, marginBottom: 20 }}
              onClick={() => { setStep('phone'); setOtp(Array(OTP_LEN).fill('')); setOtpError(''); }}
            >
              <ArrowLeft size={14} /> Change number
            </button>

            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--k-text)' }}>
                Enter the 6-digit code sent to
              </p>
              <p style={{ fontSize: 14, color: 'var(--k-primary)', fontWeight: 700, marginTop: 4 }}>
                {country.code} {phone}
              </p>
              {devOtp && (
                <div style={{ background: 'var(--k-info-bg)', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 12px', marginTop: 12, fontSize: 13, color: 'var(--k-info)' }}>
                  Dev OTP: <strong>{devOtp}</strong>
                </div>
              )}
            </div>

            <div className="k-otp-grid" onPaste={handleOtpPaste}>
              {otp.map((v, i) => (
                <input
                  key={i}
                  ref={el => otpRefs.current[i] = el}
                  className="k-otp-cell"
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={v}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                />
              ))}
            </div>

            {otpError && <div className="k-error-text" style={{ textAlign: 'center', marginTop: 10 }}>{otpError}</div>}

            <button
              className="k-btn k-btn-primary"
              style={{ width: '100%', marginTop: 20 }}
              disabled={otp.join('').length < OTP_LEN || verifying}
              onClick={verifyOtp}
            >
              {verifying ? <><span className="k-spinner k-spinner-sm" style={{ borderTopColor: '#fff' }} /> Verifying...</> : 'Verify & Sign In →'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              {countdown > 0 ? (
                <span style={{ fontSize: 13, color: 'var(--k-text-sub)' }}>Resend OTP in {countdown}s</span>
              ) : (
                <button
                  style={{ border: 'none', background: 'none', color: 'var(--k-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  onClick={sendOtp}
                  disabled={sending}
                >
                  <RefreshCw size={13} /> Resend OTP
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Staff Tab ── */}
        {tab === 'staff' && (
          <form onSubmit={handleStaffLogin}>
            <div className="k-form-group">
              <label className="k-label required">Username or Email</label>
              <input
                className="k-input"
                placeholder="Enter username or email"
                value={identifier}
                onChange={e => { setIdentifier(e.target.value); setStaffError(''); }}
                autoFocus
              />
            </div>
            <div className="k-form-group">
              <label className="k-label required">Password</label>
              <div className="k-input-group">
                <input
                  className="k-input"
                  type={showPw ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={password}
                  style={{ paddingRight: 44 }}
                  onChange={e => { setPassword(e.target.value); setStaffError(''); }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(p => !p)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--k-text-muted)' }}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {staffError && <div className="k-error-text">{staffError}</div>}
            </div>
            <button
              type="submit"
              className="k-btn k-btn-primary"
              style={{ width: '100%' }}
              disabled={staffLoading}
            >
              {staffLoading ? <><span className="k-spinner k-spinner-sm" style={{ borderTopColor: '#fff' }} /> Signing in...</> : 'Sign In →'}
            </button>
          </form>
        )}

        <div className="k-divider-text" style={{ marginTop: 24 }}>New kitchen partner?</div>
        <Link
          to="/kitchen/register"
          className="k-btn k-btn-outline"
          style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
        >
          Register your kitchen
        </Link>
      </div>
    </div>
  );
}
